import { computed, Injectable, OnDestroy, signal } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EMAIL_OCR_PREVIEW_STATUS } from '../email-app.models';
import { EmailOcrResult } from './email-ocr.service';

@Injectable()
export class EmailOcrPreviewStateService implements OnDestroy {
  private ocrStatusesSignal = signal(new Map<string, EMAIL_OCR_PREVIEW_STATUS>());
  private ocrPreviewRequest$ = new Subject<{
    attachmentId: string;
    request$: Observable<EmailOcrResult>;
  }>();
  private destroy$ = new Subject<void>();

  readonly ocrStatuses = computed(() => this.ocrStatusesSignal());

  constructor() {
    this.setupOcrPreviewStream();
  }

  private setupOcrPreviewStream(): void {
    this.ocrPreviewRequest$
      .pipe(
        tap(({ attachmentId }) => {
          this.ocrStatusesSignal.update(statuses => {
            const newStatuses = new Map(statuses);

            newStatuses.forEach((_, key) => {
              if (key !== attachmentId) {
                newStatuses.set(key, 'initial');
              }
            });

            newStatuses.set(attachmentId, 'loading');
            return newStatuses;
          });
        }),
        switchMap(({ attachmentId, request$ }) =>
          request$.pipe(
            map(response => ({ attachmentId, success: true, response })),
            catchError(error => of({ attachmentId, success: false, error }))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.ocrStatusesSignal.update(statuses => {
          const newStatuses = new Map(statuses);

          if (result.success) {
            newStatuses.set(result.attachmentId, 'previewed');
          } else {
            newStatuses.set(result.attachmentId, 'initial');
          }

          return newStatuses;
        });
      });
  }

  requestOcrPreview(attachmentId: string, request$: Observable<EmailOcrResult>): void {
    this.ocrPreviewRequest$.next({ attachmentId, request$ });
  }

  closeOcrPreview(attachmentId: string): void {
    this.ocrStatusesSignal.update(statuses => {
      const newStatuses = new Map(statuses);
      newStatuses.set(attachmentId, 'initial');
      return newStatuses;
    });
  }

  resetAllStatuses(): void {
    this.ocrStatusesSignal.update(statuses => {
      const newStatuses = new Map(statuses);
      newStatuses.forEach((_, key) => {
        newStatuses.set(key, 'initial');
      });
      return newStatuses;
    });
  }

  isLoading(attachmentId: string): boolean {
    return this.ocrStatuses().get(attachmentId) === 'loading';
  }

  isPreviewed(attachmentId: string): boolean {
    return this.ocrStatuses().get(attachmentId) === 'previewed';
  }

  getStatus(attachmentId: string): EMAIL_OCR_PREVIEW_STATUS {
    return this.ocrStatuses().get(attachmentId) || 'initial';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
